import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, Plus, Trash2, ChevronDown } from "lucide-react";

/**
 * Built-in views + user-saved views for the Pipeline page.
 * Filter shape: { stage, department, timeline, search, mine, overdue, renewalsQuarter }
 */
const BUILTIN_VIEWS = (userId) => [
  { id: "builtin-all", name: "All Contracts", filters: {}, builtin: true },
  { id: "builtin-mine", name: "My Queue", filters: { assigned_to: userId }, builtin: true },
  { id: "builtin-overdue", name: "My Overdue", filters: { assigned_to: userId, sla: "overdue" }, builtin: true },
  { id: "builtin-unassigned", name: "Unassigned", filters: { stage: "unassigned" }, builtin: true },
  { id: "builtin-renewals-q", name: "Renewals This Quarter", filters: { renewalsWithinDays: 90 }, builtin: true },
  { id: "builtin-executed", name: "Executed (Archive)", filters: { stage: "executed" }, builtin: true },
];

export default function SavedViewsMenu({ userId, currentFilters, onApply }) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState("builtin-all");
  const builtins = BUILTIN_VIEWS(userId);

  const load = () => api.get("/saved-views").then(r => setSaved(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = (view) => {
    setSelected(view.id);
    onApply(view.filters);
    setOpen(false);
  };

  const saveCurrent = async () => {
    if (!name.trim()) return toast.error("Enter a view name");
    try {
      await api.post("/saved-views", { name: name.trim(), filters: currentFilters });
      toast.success(`Saved view "${name}"`);
      setName("");
      load();
    } catch (e) { toast.error("Failed to save view"); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/saved-views/${id}`); toast.success("View deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const active = [...builtins, ...saved].find(v => v.id === selected);

  return (
    <div className="relative">
      <button
        data-testid="views-menu-btn"
        onClick={() => setOpen(v => !v)}
        className="h-8 px-2.5 border border-slate-200 rounded-md text-xs font-semibold hover:bg-slate-50 flex items-center gap-1.5"
      >
        <Bookmark className="w-3.5 h-3.5 text-[#005289]" />
        <span className="max-w-[140px] truncate">{active?.name || "Views"}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-9 w-72 bg-white border border-slate-200 rounded-md shadow-lg z-40" data-testid="views-menu">
          <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">Built-in views</div>
          {builtins.map(v => (
            <div key={v.id} onClick={() => apply(v)}
                 className={`px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer flex items-center justify-between ${selected === v.id ? "bg-blue-50 text-[#005289] font-semibold" : ""}`}
                 data-testid={`view-${v.id}`}>
              <span>{v.name}</span>
            </div>
          ))}

          <div className="px-3 py-2 border-t border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            My saved views {saved.length ? `(${saved.length})` : ""}
          </div>
          {saved.length === 0 && (
            <div className="px-3 py-3 text-[11px] text-slate-500">Save the current filter as a one-click shortcut below.</div>
          )}
          {saved.map(v => (
            <div key={v.id} onClick={() => apply(v)}
                 className={`px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer flex items-center justify-between group ${selected === v.id ? "bg-blue-50 text-[#005289] font-semibold" : ""}`}
                 data-testid={`view-saved-${v.id}`}>
              <span className="truncate">{v.name}</span>
              <button onClick={(e) => remove(v.id, e)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" title="Delete">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}

          <div className="p-2 border-t border-slate-100 flex gap-1.5">
            <input value={name} onChange={e => setName(e.target.value)}
                   placeholder="Save current filters as..." className="flex-1 h-7 px-2 text-xs border border-slate-200 rounded"
                   data-testid="save-view-name" />
            <button onClick={saveCurrent}
                    className="h-7 px-2 rounded text-white text-xs font-semibold flex items-center gap-1"
                    style={{ background: "#005289" }} data-testid="save-view-btn">
              <Plus className="w-3 h-3" /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { BUILTIN_VIEWS };
